// The selection console: a StarCraft-style panel along the bottom of the screen that
// shows whatever is selected in a raid. One unit or building gets a portrait, its name
// and the real system it is modelled on, a health bar, its weapon's numbers and what it
// is doing right now. A group gets a grid of cards, one per unit with its own health,
// and clicking a card narrows the selection to that unit (Shift-click drops it). It is
// DOM, redrawn a few times a second, so it stays crisp and tests can read it.
import { DEFAULT_CONFIG, THREAT_NAMES, TICKS_PER_SECOND, TILE, threatOf, type BuildingSnapshot, type Threat, type UnitSnapshot } from '@thrash/sim';
import { kindOf, silhouetteMarkup, type Kind } from './icons.ts';

const el = document.getElementById('console')!;
const portrait = el.querySelector<HTMLElement>('.portrait')!;
const nameEl = el.querySelector<HTMLElement>('.name')!;
const realEl = el.querySelector<HTMLElement>('.real')!;
const roleEl = el.querySelector<HTMLElement>('.role')!;
const hpEl = el.querySelector<HTMLElement>('.hp')!;
const barEl = el.querySelector<HTMLElement>('.hpline .bar > i')!;
const statsEl = el.querySelector<HTMLElement>('.stats')!;
const statusEl = el.querySelector<HTMLElement>('.status')!;
const single = el.querySelector<HTMLElement>('.single')!;
const grid = el.querySelector<HTMLElement>('.grid')!;

const icon = (kind: Kind, color: string): string => silhouetteMarkup(kind, color);
const kindOfUnit = (type: string): Kind => kindOf(type);

const ATTACKER = '#8fbf5a', DEFENDER = '#e0785f';

function setHp(hp: number, maxHp: number): void {
  hpEl.textContent = `${Math.max(0, hp)} / ${maxHp} hp`;
  const frac = maxHp > 0 ? Math.max(0, Math.min(1, hp / maxHp)) : 0;
  barEl.style.width = `${Math.round(frac * 100)}%`;
  barEl.className = frac > 0.5 ? 'ok' : frac > 0.25 ? 'hurt' : 'low';
}

/** Rounds a minute for a weapon that fires `burst` rounds `burstGap` ticks apart, then waits `attackTicks`. */
function roundsPerMinute(w: { attackTicks?: number; burst?: number; burstGap?: number }): number {
  const burst = w.burst ?? 1;
  const cycle = (burst - 1) * (w.burstGap ?? 1) + (w.attackTicks ?? 1);
  return Math.round((burst / cycle) * TICKS_PER_SECOND * 60);
}

function stats(rows: [string, string][]): void {
  statsEl.replaceChildren(...rows.flatMap(([k, v]) => {
    const dt = document.createElement('dt'); dt.textContent = k;
    const dd = document.createElement('dd'); dd.textContent = v;
    return [dt, dd];
  }));
}

const threats = (list: readonly Threat[]): string => list.map(t => THREAT_NAMES[t]).join(', ');

/** One attacker unit, or a defender's patrol. */
export function showUnit(u: UnitSnapshot): void {
  const def = DEFAULT_CONFIG.units[u.type];
  el.hidden = false;
  single.hidden = false;
  grid.hidden = true;
  portrait.innerHTML = icon(kindOfUnit(u.type), u.defender ? DEFENDER : ATTACKER);
  nameEl.textContent = u.hp > 0 ? (def?.name ?? u.type) : `${def?.name ?? u.type} · destroyed`;
  realEl.textContent = def?.real ?? '';
  roleEl.textContent = def?.role ?? '';
  setHp(u.hp, u.maxHp);
  if (!def) { stats([]); statusEl.textContent = ''; return; }
  const tiles = (n: number) => `${(n / TILE).toFixed(1)}-tile`;
  const rows: [string, string][] = [];
  if (def.recon) rows.push(['Sees', `${(def.range / TILE).toFixed(1)} tiles: mines and defences`]);
  else if (def.warhead || def.deliveredBy) rows.push(['Warhead', def.warhead?.radius ? `${def.warhead.damage} dmg, ${tiles(def.warhead.radius)} blast` : 'Nuclear']);
  else if (def.oneWay) rows.push(['Warhead', `${def.damage}${def.antiArmor ? ` (× ${def.antiArmor} vs armour)` : ''}${def.splash ? `, ${tiles(def.splash)} blast` : ''}, one way`]);
  else if (def.jet && def.gun) {
    rows.push(['Missiles', `2 × ${def.damage}, ${tiles(def.splash ?? 0)} blast, every ${def.attackTicks / TICKS_PER_SECOND}s from ${(def.range / TILE).toFixed(0)} tiles`]);
    rows.push(['Cannon', `${def.gun.damage} every ${(def.gun.everyTicks / TICKS_PER_SECOND).toFixed(1)}s inside ${(def.gun.range / TILE).toFixed(0)} tiles`]);
  } else if (def.damage > 0) {
    rows.push(['Damage', `${def.damage}${(def.burst ?? 1) > 1 ? ` × ${def.burst} burst` : ''}${def.antiArmor ? ` (× ${def.antiArmor} vs armour)` : ''}${def.splash ? `, ${tiles(def.splash)} blast` : ''}`]);
    rows.push(['Fire rate', `${roundsPerMinute(def)} rounds/min`]);
    rows.push(['Range', `${(def.range / TILE).toFixed(1)} tiles`]);
  }
  if (def.speed > 0) rows.push(['Speed', `${((def.speed * TICKS_PER_SECOND) / TILE).toFixed(1)} tiles/s`]);
  rows.push(['Class', THREAT_NAMES[threatOf(def)]]);
  if (def.infantry) rows.push(['Charge', def.sniper ? 'Plants one once no crew is left to shoot' : 'Plants one at arm\'s length, 4 s fuse']);
  if (def.sniper) rows.push(['Shoots', 'Crews of manned nests and posts']);
  if (def.evasion) rows.push(['Evasion', `Shakes off ${Math.round(def.evasion * 100)}% of fire`]);
  if (def.interceptChance) rows.push(['Point defence', `Downs ${Math.round(def.interceptChance * 100)}% of missiles`]);
  if (def.carries) rows.push(['Carries', Object.entries(def.carries.units).map(([t, n]) => `${n} ${DEFAULT_CONFIG.units[t]?.name ?? t}`).join(', ')]);
  if (def.launches) rows.push(['Launches', `${DEFAULT_CONFIG.units[def.launches.unit]?.name ?? def.launches.unit} every ${def.launches.everyTicks / TICKS_PER_SECOND}s`]);
  if (def.launchedFrom) rows.push(['Needs', `${DEFAULT_CONFIG.units[def.launchedFrom]?.name ?? def.launchedFrom} on the field`]);
  if (def.cannotHit?.length) rows.push(['Cannot hit', threats(def.cannotHit)]);
  if (def.electronic) rows.push(['Electronic', 'An EMP drops it']);
  stats(rows);
  const status = u.hp <= 0 ? 'Destroyed'
    : u.fuse !== undefined ? `Impact in ${(u.fuse / TICKS_PER_SECOND).toFixed(1)}s`
    : u.disabled ? 'Knocked out by EMP'
    : u.ordered ? 'Following your order'
    : u.reload !== undefined ? 'Reloading'
    : u.defender ? 'Patrolling' : 'Fighting on its own';
  statusEl.textContent = status;
}

/** A building, with its defences' numbers. `priority` is the order the defender set, if any. */
export function showBuilding(b: BuildingSnapshot, priority?: readonly Threat[]): void {
  const def = DEFAULT_CONFIG.buildings[b.type];
  el.hidden = false;
  single.hidden = false;
  grid.hidden = true;
  portrait.innerHTML = icon(def?.kind === 'defence' ? 'defence' : 'building', DEFENDER);
  nameEl.textContent = b.hp > 0 ? (def?.name ?? b.type) : `${def?.name ?? b.type} · destroyed`;
  realEl.textContent = def?.real ?? '';
  roleEl.textContent = def?.manned ? 'Its crew fights in the open: a marksman can pick them off' : def?.fortified ? 'Concrete: small arms do half damage' : '';
  setHp(b.hp, b.maxHp);
  const rows: [string, string][] = [];
  if (def?.damage !== undefined && def.range !== undefined && !def.mine) {
    rows.push(['Damage', `${def.damage}${(def.burst ?? 1) > 1 ? ` × ${def.burst} burst` : ''}${def.splash ? ` · ${(def.splash / TILE).toFixed(1)}-tile blast` : ''}`]);
    rows.push(['Fire rate', `${roundsPerMinute(def)} ${def.projectile ? 'missiles' : 'rounds'}/min`]);
    rows.push(['Range', `${def.minRange ? `${(def.minRange / TILE).toFixed(0)}–` : ''}${(def.range / TILE).toFixed(1)} tiles`]);
  }
  if (def?.targets && !def.mine) rows.push(['Engages', threats(def.targets)]);
  const order = priority?.length ? priority : def?.defaultPriority;
  if (order?.length) rows.push(['First', threats(order)]);
  if (b.ammo !== undefined) rows.push(['Ammo', `${b.ammo} left`]);
  if (b.garrison !== undefined) rows.push(['Squad', `${b.garrison} of ${def?.garrison ?? b.garrison} fighting`]);
  stats(rows);
  statusEl.textContent = b.hp <= 0 ? 'Destroyed'
    : b.disabled ? 'Knocked out by EMP'
    : b.outOfAmmo ? 'Out of ammo'
    : b.marked ? 'Spotted by a recon drone: takes 25% more damage'
    : b.reload !== undefined ? 'Reloading' : '';
}

/**
 * Several units: a card for each, with its own health. Clicking a card selects just that unit;
 * Shift-click takes it out of the selection.
 */
export function showGroup(units: readonly UnitSnapshot[], pick: (id: number, remove: boolean) => void): void {
  el.hidden = false;
  single.hidden = true;
  grid.hidden = false;
  const cards = units.slice(0, 36).map(u => {
    const b = document.createElement('button');
    b.className = 'card';
    const frac = Math.max(0, Math.min(1, u.hp / u.maxHp));
    b.innerHTML = `${icon(kindOfUnit(u.type), ATTACKER)}<i class="bar"><i class="${frac > 0.5 ? 'ok' : frac > 0.25 ? 'hurt' : 'low'}" style="width:${Math.round(frac * 100)}%"></i></i>`;
    b.title = `${DEFAULT_CONFIG.units[u.type]?.name ?? u.type} · ${u.hp} / ${u.maxHp} hp`;
    b.onclick = e => pick(u.id, e.shiftKey);
    return b;
  });
  const more = units.length > 36 ? [Object.assign(document.createElement('span'), { className: 'muted', textContent: `+${units.length - 36}` })] : [];
  grid.replaceChildren(...cards, ...more);
}

export function hideConsole(): void {
  el.hidden = true;
}
